import type { OutlineItem } from "@/lib/blocks";
import { cn } from "@/lib/utils";

/**
 * The page rail ("On this page" on notes, the component list on the docs page) and the
 * page frame that makes room for it. Widths are those of <main> (the `main` container in
 * Layout), so a collapsed sidebar counts as room:
 *
 * - below 60rem the rail is hidden;
 * - from 60rem it takes a grid column beside the content;
 * - from 82rem there is room on both sides of a centred 48rem column, so the rail floats
 *   in the gutter and the content sits where it would without a rail.
 *
 * The note view and the editors share the frame so the content column sits at the same x
 * whether you are reading or editing: switching must not jump.
 */
export type RailSide = "left" | "right";

export function hasRail(outline: OutlineItem[] | undefined): boolean {
  return (outline?.length ?? 0) >= 2;
}

export function frameClass(rail: boolean, side: RailSide = "right"): string {
  if (!rail) return "mx-auto max-w-3xl";
  return cn(
    "relative mx-auto max-w-5xl @min-[60rem]/main:grid @min-[60rem]/main:gap-10 @min-[82rem]/main:block @min-[82rem]/main:max-w-3xl",
    side === "right" ? "@min-[60rem]/main:grid-cols-[minmax(0,1fr)_13rem]" : "@min-[60rem]/main:grid-cols-[13rem_minmax(0,1fr)]",
  );
}

export function columnClass(rail: boolean): string {
  return cn("min-w-0", rail && "@min-[60rem]/main:max-w-3xl");
}

export interface RailItem {
  id: string;
  text: string;
  depth?: number;
}

export function PageRail({
  title,
  items,
  active,
  links = true,
  side = "right",
  label,
}: {
  title: string;
  items: RailItem[];
  /** Id of the item to highlight. */
  active?: string;
  /** False in the editor: headings are listed but there is nothing to scroll to. */
  links?: boolean;
  side?: RailSide;
  /** Accessible name of the nav; defaults to the title. */
  label?: string;
}) {
  return (
    <aside
      className={cn(
        "hidden @min-[60rem]/main:block @min-[82rem]/main:absolute @min-[82rem]/main:inset-y-0 @min-[82rem]/main:w-52",
        side === "right" ? "@min-[82rem]/main:left-full @min-[82rem]/main:ml-10" : "order-first @min-[82rem]/main:right-full @min-[82rem]/main:mr-10",
      )}
    >
      <nav className="sticky top-8" aria-label={label ?? title}>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
        <ul className="border-l text-sm">
          {items.map((o) => {
            const style = { paddingLeft: 12 + Math.max(0, (o.depth ?? 1) - 1) * 12 };
            const base = "-ml-px block truncate border-l border-transparent py-1 pr-2 text-muted-foreground";
            return (
              <li key={o.id}>
                {links ? (
                  <a
                    href={`#${o.id}`}
                    style={style}
                    aria-current={active === o.id ? "location" : undefined}
                    className={cn(base, "transition-colors hover:border-foreground hover:text-foreground", active === o.id && "border-foreground text-foreground")}
                  >
                    {o.text}
                  </a>
                ) : (
                  <span style={style} className={base}>
                    {o.text}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}

export function OutlineRail({ outline, activeHash, links = true }: { outline: OutlineItem[]; activeHash?: string; links?: boolean }) {
  return <PageRail title="On this page" items={outline} active={activeHash?.replace(/^#/, "")} links={links} />;
}
